<template>
	<div
		v-if="label || iconToken"
		:class="{
			'radio--disabled' : disabled,
			'radio--selected' : checked,
			'radio--hover' : hover || innerHover,
		}"
		class="radio"
		data-cy="radio"
		role="radio"
		:aria-checked="checked"
		:tabindex="tabIndex"
		:aria-hidden="ariaHidden"
		@mouseover="innerHover = true"
		@mouseleave="innerHover = false"
		@click="check"
		@keyup.space="check"
		@keyup.enter="check"
	>
		<input
			v-model="model"
			:value="value"
			:disabled="disabled"
			type="radio"
			class="radio__input"
			@change="check"
		>

		<span
			:class="{
				'is-animating' : animating
			}"
			class="radio__check"
		/>

		<label class="radio__label">
			<helio-icon
				v-if="iconToken"
				:weight="iconWeight"
				:token="iconToken"
			/>

			{{ label }}
		</label>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'Radio',

	components: {
		'helio-icon': Icon,
	},

	props: {
		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Radio button will be disabled for the user',
		},

		hover: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the radio--hover CSS class will be applied to Radio',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether an icon will appear next to the Radio button and which icon will appear',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		initialChecked: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets if Radio button is initially checked',
		},

		initialModel: {
			type: [String, Number, Boolean],
			required: false,
			default: null,
			description: 'Sets which grouping of Radio buttons the Radio button belongs to (so that only one may be selected at a time)',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Radio button\'s label',
		},

		value: {
			type: [String, Number, Boolean],
			required: false,
			default: null,
			description: 'Sets the value the Radio button will emit if the Radio button is selected',
		},
	},

	data() {
		return {
			animating: false,
			model: null,
			tabIndex: '0',
			checked: false,
			ariaHidden: false,
			innerHover: false,
		};
	},

	watch: {
		initialChecked(checked) {
			if (this.checked !== checked) {
				this.model = this.value;
				this.checked = checked;
			}
		},

		initialModel(model) {
			if (this.checked && this.model !== model) {
				this.uncheck();
			}
		},

		disabled(isDisabled) {
			this.updateDisabledState(isDisabled);
		},
	},

	created() {
		this.model = this.initialModel;
		this.checked = this.initialChecked;

		this.updateDisabledState(this.disabled);
	},

	methods: {
		updateDisabledState(isDisabled) {
			if (isDisabled) {
				this.tabIndex = '-1';
				this.ariaHidden = true;

				// uncheck the radio when it's disabled
				if (this.checked) {
					this.checked = false;
				}
			} else {
				this.tabIndex = '0';
				this.ariaHidden = false;

				// when de-disabled, re-check the radio if it should be
				if (!this.checked && this.initialModel === this.value) {
					this.check(false);
				}
			}
		},

		check(emit = true) {
			if (!this.disabled && !this.checked) {
				this.model = this.value;

				if (emit) {
					this.$emit('changed', this.model);
				}

				this.checked = true;
				this.animate();
			}
		},

		uncheck() {
			if (!this.disabled) {
				this.checked = false;
				this.animate();
			}
		},

		animate() {
			this.animating = true;

			setTimeout(() => {
				this.animating = false;
			}, 200);
		},
	},
};
</script>

<style lang="scss" src="./radio.scss" />
